import { useMemo } from "react";
import type { VisualEdge, VisualNode } from "@/types/knowledgeGraph";
import {
	calculateBetweennessCentrality,
	calculateDegreeCentrality,
	calculateEigenvectorCentrality,
	calculateNodeSizes,
	detectCommunitiesBFS,
	getCommunityColor,
} from "@/utils/graphAnalysis";

interface GraphMetrics {
	communities: Map<string, number>;
	degree: Map<string, number>;
	betweenness: Map<string, number>;
	eigenvector: Map<string, number>;
	nodeSizes: Map<string, number>;
}

interface UseGraphMetricsOptions {
	dataNodes: VisualNode[];
	dataEdges: VisualEdge[];
}

interface UseGraphMetricsReturn {
	graphMetrics: GraphMetrics;
	visualNodes: VisualNode[];
	visualEdges: VisualEdge[];
}

export const useGraphMetrics = ({ dataNodes, dataEdges }: UseGraphMetricsOptions): UseGraphMetricsReturn => {
	const graphMetrics = useMemo<GraphMetrics>(() => {
		if (dataNodes.length === 0 || dataEdges.length === 0) {
			return {
				communities: new Map<string, number>(),
				degree: new Map<string, number>(),
				betweenness: new Map<string, number>(),
				eigenvector: new Map<string, number>(),
				nodeSizes: new Map<string, number>(),
			};
		}

		const tempNodes: VisualNode[] = dataNodes.map((node) => ({
			...node,
			radius: 8,
		}));
		const tempEdges: VisualEdge[] = dataEdges.map((edge) => ({
			...edge,
			strength: 0.3,
		}));

		const communities = detectCommunitiesBFS(tempNodes, tempEdges);
		const degree = calculateDegreeCentrality(tempNodes, tempEdges);
		const betweenness = calculateBetweennessCentrality(tempNodes, tempEdges);
		const eigenvector = calculateEigenvectorCentrality(tempNodes, tempEdges);
		const nodeSizes = calculateNodeSizes(tempNodes, degree, betweenness, eigenvector);

		return {
			communities,
			degree,
			betweenness,
			eigenvector,
			nodeSizes,
		};
	}, [dataNodes, dataEdges]);

	const visualNodes = useMemo(() => {
		return dataNodes.map((node) => {
			const communityId = graphMetrics.communities.get(node.id) ?? 0;
			const nodeSize = graphMetrics.nodeSizes.get(node.id) ?? 10;

			return {
				...node,
				radius: nodeSize / 2,
				color: getCommunityColor(communityId),
				group: communityId,
			};
		});
	}, [dataNodes, graphMetrics.communities, graphMetrics.nodeSizes]);

	const visualEdges = useMemo(() => {
		return dataEdges.map((edge) => ({
			...edge,
			strength: 0.3,
			inferred: edge.inferred ?? false,
		}));
	}, [dataEdges]);

	return {
		graphMetrics,
		visualNodes,
		visualEdges,
	};
};

export default useGraphMetrics;
